// QUEST-2622 — the Home page renders BE-driven `healthMetricRangeCard` nodes
// (the lab-results markers). The dashboard SDK registers that renderer as a
// side-effect of module load and does not export it individually, so without
// eager-loading the module here it only gets registered after the user visits
// Lab Results / Insights — leaving the Home widget unrendered on initial load.
// Follow-up: have the SDK expose an init to drop this eager import.
import "@leagueplatform/dashboard";
import { driver } from "@leagueplatform/masonry";
import {
  MasonryEngineNode,
  MasonryEngineNodeRenderer,
} from "@leagueplatform/masonry-engine";
import { MASONRY } from "common/constants/masonry-constants";
import { MasonryBrandingBannerRenderer } from "components/masonry-branding-banner/masonry-branding-banner-renderer.component";
import { MasonryBulletedListItemRenderer } from "components/masonry-bulleted-list-item/masonry-bulleted-list-item-renderer.component";
import { MasonryCareLibrarySearchFieldRenderer } from "components/masonry-care-library-search-field/masonry-care-library-search-field-renderer.component";
import { MasonryFeatureHighlightFlowRenderer } from "components/masonry-feature-highlight-flow/masonry-feature-highlight-flow-renderer.component";
import { MasonryFeatureHighlightStepRenderer } from "components/masonry-feature-highlight-step/masonry-feature-highlight-step-renderer.component";
import { MasonrySmallCardRendererOverride } from "components/masonry-small-card/masonry-small-card-renderer.component";
import { QuestDeeplinkActionHandler } from "utils/masonry-deeplink-action-handler";
import { QuestDownloadPhysicianFormActionHandler } from "utils/masonry-download-physician-form-action-handler";
import { QuestStartSchedulingFlowActionHandler } from "utils/scheduling-flow/masonry-start-scheduling-flow-action-handler";
import { QuestOpenUploadFormModalActionHandler } from "utils/upload-physician-form/masonry-action-handler";

const DEEPLINK_ACTION_TYPE = "deeplink";
const DOWNLOAD_PHYSICIAN_FORM_ACTION_TYPE = "downloadPhysicianForm";
const OPEN_UPLOAD_FORM_MODAL_ACTION_TYPE = "openUploadFormModal";
const START_SCHEDULING_FLOW_ACTION_TYPE = "startSchedulingFlow";

const registerCustomRenderer = (
  nodeType: string,
  renderer: MasonryEngineNodeRenderer<MasonryEngineNode<string, any>>,
  namespace: string = MASONRY.DEFAULT_NAMESPACE
) => {
  driver.nodeRendererRegistry.registerRenderer(nodeType, namespace, renderer);
};

const overrideCustomRenderer = (
  nodeType: string,
  renderer: MasonryEngineNodeRenderer<MasonryEngineNode<string, any>>
) => {
  driver.nodeRendererRegistry.overrideRenderer(
    nodeType,
    MASONRY.DEFAULT_NAMESPACE,
    renderer
  );
};

export const initMasonryRegistry = () => {
  registerCustomRenderer(
    MASONRY.NODE_TYPE_BULLETED_LIST_ITEM,
    MasonryBulletedListItemRenderer
  );
  overrideCustomRenderer(
    MASONRY.NODE_TYPE_LINEAR_FLOW_STEP,
    MasonryFeatureHighlightStepRenderer
  );

  // QUEST-2329 — force Back button to `priority: "secondary"` (outlined pill)
  // on Feature Highlight steps. BE config uses `tertiary` which renders
  // text-only; Figma requires the outlined treatment.
  overrideCustomRenderer(
    MASONRY.NODE_TYPE_LINEAR_FLOW,
    MasonryFeatureHighlightFlowRenderer
  );

  // QUEST-2626 — wire non-deeplink `smallCard` onClick actions the SDK drops.
  overrideCustomRenderer(
    MASONRY.NODE_TYPE_SMALL_CARD,
    MasonrySmallCardRendererOverride
  );

  // Scope a `heroBanner` renderer to the `brandingBanner` namespace so the
  // homepage co-branding strip (BE: `homepage/brandingBanner.yaml`) renders
  // as a thin grey strip per Figma, instead of being picked up by the
  // default `MasonryHeroBannerRenderer` which assumes a full hero layout.
  // Other hero banners (e.g. the greeting hero) keep the default renderer.
  registerCustomRenderer(
    MASONRY.NODE_TYPE_HERO_BANNER,
    MasonryBrandingBannerRenderer,
    MASONRY.NAMESPACE_BRANDING_BANNER
  );

  // BE `careLibrarySearchField` powers the search bar at the top of the
  // Journey Explore tab (`careLibrary$root`). iOS and Android already render
  // it; without this registration the Web payload arrives but Masonry skips
  // it silently. Scoped to the `care_library_search` namespace defined in
  // services `quest/careLibrary/careLibrarySearch.pkl`.
  registerCustomRenderer(
    MASONRY.NODE_TYPE_CARE_LIBRARY_SEARCH_FIELD,
    MasonryCareLibrarySearchFieldRenderer,
    MASONRY.NAMESPACE_CARE_LIBRARY_SEARCH
  );

  // Override the default `deeplink` handler so `tel:` / `mailto:` / `sms:`
  // URIs invoke the native app picker even when the click originates from
  // a JS-wired card action (e.g. BE `callServiceCenterCard`). See the
  // handler's docblock for the full rationale.
  //
  // `registerUniversalHandler` (vs `registerHandler`) is required here —
  // the SDK pre-registers the default handler on the `default` namespace,
  // so namespace-scoped `registerHandler` throws. Universal handlers
  // supersede namespace handlers by default.
  driver.actionController.registerUniversalHandler(
    DEEPLINK_ACTION_TYPE,
    QuestDeeplinkActionHandler
  );

  // Register the Quest-specific `downloadPhysicianForm` custom action so the
  // BE-driven "Download Form" CTA on `screenings$selfScreening$downloadForm$root`
  // fetches the PDF, triggers a native browser download, and flips the
  // `{planYearEndDate}-physician-form-downloaded` user custom field. Mirrors
  // Yuri's Android implementation in quest-android#94 (QUEST-1066).
  driver.actionController.registerUniversalHandler(
    DOWNLOAD_PHYSICIAN_FORM_ACTION_TYPE,
    QuestDownloadPhysicianFormActionHandler
  );

  // QUEST-1695 — opens the client-owned upload modal from the BE "Upload
  // Form" CTA on `screenings$selfScreening$uploadForm$root`.
  driver.actionController.registerUniversalHandler(
    OPEN_UPLOAD_FORM_MODAL_ACTION_TYPE,
    QuestOpenUploadFormModalActionHandler
  );

  // QUEST-2796 — override the SDK's `startSchedulingFlow` so the DP POST
  // runs before the overlay opens. Requires <LazyQuestSchedulingFlowBridge />
  // to be mounted (it wires the SDK's openOverlay into the handler).
  driver.actionController.registerUniversalHandler(
    START_SCHEDULING_FLOW_ACTION_TYPE,
    QuestStartSchedulingFlowActionHandler
  );
};
